import * as Duo from '.';
import { DuoItem } from '../care/care.ruleset';
import { PetSpeciesID } from '../pet';
import * as Pet from '../pet';
import createAsyncGetters from '../config/asyncGetters/createAsyncGetters';
import * as Visit from '../visit';
import { getAlternativeWebIABBucket } from '../chatbot/chatbot.util';
import {
  getPendingReceivedDuoInvitationFromFriend,
  getPendingSentDuoInvitationToFriend,
} from './duo.getters';
import { PlayerID } from '../friends/friends.ruleset';
import { getPlayerProfileFromLocale } from '../profile/profile.getters';
import { getLocale } from '../getters/user';

export const duoAsyncGetters = createAsyncGetters({
  getAllDuoInvitations: async (playerId: string, api) => {
    const { state } = (await api.fetchStates([playerId]))[playerId];
    const now = api.date.now();

    return [
      ...Duo.getters.getNonExpiredSentInvitations(state, now),
      ...Duo.getters.getNonExpiredReceivedInvitations(state, now),
    ];
  },
  getDuoInvitationsDetails: async (
    _: void,
    api,
  ): Promise<{ sent: DuoFriend[]; received: DuoFriend[] }> => {
    const state = await api.getOwnState();
    const { sent, received } = state.duo.invitations;
    const { getVisitsCount, getVisitedCount } = Visit.getters;

    const userIds = [
      ...sent.map(({ playerId }) => playerId),
      ...received.map(({ playerId }) => playerId),
    ];

    const profiles = await api.searchPlayers({
      where: {
        id: {
          isOneOf: userIds,
        },
      },
      limit: sent.length + received.length,
    });

    const playersStates = await api.fetchStates(userIds);

    const map = (original: (typeof sent)[number]): DuoFriend => {
      const profile = profiles.results.find((x) => x.id === original.playerId);

      const friendState = playersStates[original.playerId].state;

      return {
        id: original.playerId,
        // There is case where player state was deleted but invitation was not
        hasState: !!friendState,
        hasPet: !!Pet.getters.getCurrentEvolution(friendState)?.species,
        profile: {
          name: profile?.publicProfileName ?? 'unknown',
          photo: profile?.publicProfilePhoto ?? '',
          lastLogin: (profile as any)?.updatedAt ?? 0,
          visits: getVisitsCount(state, original.playerId),
          visited: getVisitedCount(state, original.playerId),
        },
        pet: {
          name: original.pet.name ?? 'unknown',
          species: (original.pet.species ?? 'giraffe') as PetSpeciesID,
          age: original.pet.age ?? 0,
        },
        invitation: {
          timestamp: original.timestamp,
          item: original.careItemId as DuoItem,
          status: original.status,
          lastReminder: original.lastReminder,
          message: original.message,
        },
      };
    };

    return {
      sent: sent.map(map).reverse(),
      received: received.map(map).reverse(),
    };
  },

  getDuoInvitationDetails: async (
    friendId: PlayerID,
    api,
  ): Promise<DuoFriend> => {
    const state = await api.getOwnState();
    const locale = getLocale(state);
    const now = api.date.now();

    // Find invitation from either sent or received invitations
    const invitation =
      getPendingSentDuoInvitationToFriend(state, now, friendId) ??
      getPendingReceivedDuoInvitationFromFriend(state, now, friendId);

    if (!invitation) {
      throw new Error('Duo invitation not found');
    }

    const { getVisitsCount, getVisitedCount } = Visit.getters;
    const { state: friendState } = (await api.fetchStates([friendId]))[
      friendId
    ];

    const profile = getPlayerProfileFromLocale(
      locale,
      friendState.publicProfile?.name,
      friendState.publicProfile?.photo,
      friendState.profile?.name,
      friendState.profile?.photo,
    );

    return {
      id: invitation.playerId,
      // There is case where player state was deleted but invitation was not
      hasState: !!friendState,
      hasPet: !!Pet.getters.getCurrentEvolution(friendState)?.species,
      profile: {
        name: profile.name,
        photo: profile.photo,
        lastLogin: friendState.updatedAt ?? 0,
        visits: getVisitsCount(state, invitation.playerId),
        visited: getVisitedCount(state, invitation.playerId),
      },
      pet: {
        name: invitation.pet.name ?? 'unknown',
        species: (invitation.pet.species ?? 'giraffe') as PetSpeciesID,
        age: invitation.pet.age ?? 0,
      },
      invitation: {
        timestamp: invitation.timestamp,
        item: invitation.careItemId as DuoItem,
        status: invitation.status,
        lastReminder: invitation.lastReminder,
        message: invitation.message,
      },
    };
  },

  getPetSpecies: async (playerId: string, api) => {
    const { state } = (await api.fetchStates([playerId]))[playerId];

    return {
      species: Pet.getters.getCurrentEvolution(state)?.species,
      name: Pet.getters.getPetName(state) ?? '',
      age: Pet.getters.getPetAge(state, api.date.now()) ?? 0,
      alternativeWebIABBucket: getAlternativeWebIABBucket(),
    };
  },

  hasPendingDebut: async (playerId: string, api) => {
    const { state } = (await api.fetchStates([playerId]))[playerId];
    return !!state.duo.debut;
  },
});

export type DuoFriend = {
  id: string;
  hasState: boolean;
  hasPet: boolean;
  profile: {
    name: string;
    photo: string;
    visits: number;
    visited: number;
    lastLogin: number;
  };
  pet: {
    name: string;
    species: PetSpeciesID;
    age: number;
  };
  invitation: {
    item: DuoItem;
    timestamp: number;
    status: 'pending' | 'accepted' | 'cancelled' | 'declined';
    lastReminder: number;
    message?: string;
  };
};
